import { type RefObject, useEffect } from 'react';

type UseScrollToActiveProps = {
	activeIndex: number;
	resultRefs: RefObject<(HTMLAnchorElement | null)[]>;
	scrollViewportRef: RefObject<HTMLDivElement>;
};

export const useScrollToActive = ({
	activeIndex,
	resultRefs,
	scrollViewportRef,
}: UseScrollToActiveProps): void => {
	useEffect(() => {
		if (activeIndex < 0) {
			return;
		}

		const el = resultRefs.current?.[activeIndex];
		const scrollViewport = scrollViewportRef.current;
		if (!el || !scrollViewport) {
			return;
		}

		// Find the actual scrollable container by traversing up the DOM tree
		let scrollableContainer: HTMLElement | null = scrollViewport;
		while (scrollableContainer) {
			const style = window.getComputedStyle(scrollableContainer);
			const overflowY = style.overflowY;
			if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
				break;
			}
			scrollableContainer = scrollableContainer.parentElement;
		}

		// Fallback to scrollViewport if no scrollable parent found
		if (!scrollableContainer) {
			scrollableContainer = scrollViewport;
		}

		const containerRect = scrollableContainer.getBoundingClientRect();
		const elRect = el.getBoundingClientRect();
		const isAbove = elRect.top < containerRect.top;
		const isBelow = elRect.bottom > containerRect.bottom;
		if (isAbove || isBelow) {
			el.scrollIntoView({ block: 'nearest' });
		}
	}, [activeIndex, resultRefs, scrollViewportRef]);
};
